import { Box, Icon, PropertyCard } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronRightFilled } from '@zillow/constellation-icons';

import { LoadingImage, LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';

import {
  getFormattedAddress,
  getPhotoThumbUrl,
  getPropertyTypeLabel,
  getUnitSummary,
} from '../../../utils/flexiblePropertyCardUtils';

import { headerStyles, infoBlockStyles, overflowMenuStyles, photoContainerStyles } from '../styles';

import { FlexiblePropertyCardOverflowMenu } from './FlexiblePropertyCardOverflowMenu';

import type { AccountViewListingGroupDto, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listing: AccountViewListingGroupDto;
  isExpanded: boolean;
  isLoading?: boolean;
  onArchiveClick: (listingAlias: string, address: RentalManagerAddressDto) => void;
};

const FlexiblePropertyCardHeader = ({ listing, isExpanded, isLoading = false, onArchiveClick }: Props) => {
  const propertyTypeLabel = getPropertyTypeLabel(listing);
  const formattedAddress = getFormattedAddress(listing);
  const unitSummary = getUnitSummary(listing);
  const photoUrl = getPhotoThumbUrl(listing.photoKey);

  const handleEditPropertyClick = () => {
    // TODO: add code
  };

  const handleArchivePropertyClick = () => {
    onArchiveClick(listing.listingAlias, listing.address);
  };

  return (
    <Box css={headerStyles} data-testid="FlexiblePropertyCard-Header">
      <Icon
        render={isExpanded ? <IconChevronDownFilled /> : <IconChevronRightFilled />}
        size="sm"
        aria-hidden={true}
        css={{ flexShrink: 0 }}
      />

      <Box css={photoContainerStyles} data-loading={isLoading || null}>
        {isLoading ? (
          <LoadingImage isLoading={true} loadingWidth="100%" loadingHeight="100%" />
        ) : (
          <PropertyCard.Photo
            src={photoUrl}
            alt={formattedAddress}
            fallbackImg={<PropertyCard.PhotoFallback />}
            css={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </Box>

      <Box css={infoBlockStyles}>
        <LoadingText textStyle="body-xs-bold" isLoading={isLoading} loadingWidth="60px" css={{ color: 'text.subtle' }}>
          {propertyTypeLabel}
        </LoadingText>

        <LoadingParagraph textStyle="body-bold" isLoading={isLoading} loadingWidth="80%">
          {formattedAddress}
        </LoadingParagraph>

        <LoadingParagraph textStyle="body-sm" isLoading={isLoading} loadingWidth="120px" css={{ color: 'text.subtle' }}>
          {unitSummary}
        </LoadingParagraph>
      </Box>

      <Box css={overflowMenuStyles}>
        <FlexiblePropertyCardOverflowMenu
          triggerLabel="Property options"
          items={[
            { label: 'Edit property', onSelect: handleEditPropertyClick },
            ...(listing.listingAlias ? [{ label: 'Archive property', onSelect: handleArchivePropertyClick }] : []),
          ]}
        />
      </Box>
    </Box>
  );
};

export default FlexiblePropertyCardHeader;
